import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import GlobalStyles from "@mui/material/GlobalStyles";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

export const MOBILE_BAR_HEIGHT = 56;
const MENU_WIDTH = 288;
const NAVY = "#18253E";

/**
 * Styles that only apply while the phone layout is mounted. They make room for
 * the fixed top bar and let tab rows scroll sideways instead of clipping.
 */
const PHONE_GLOBAL_STYLES = {
	body: { paddingTop: MOBILE_BAR_HEIGHT },
	".MuiTabs-scroller": {
		overflowX: "auto !important",
		scrollbarWidth: "none",
	},
	".MuiTabs-scroller::-webkit-scrollbar": { display: "none" },
	".MuiTab-root": { whiteSpace: "nowrap" },
} as const;

interface MobileNavBarProps {
	brand: ReactNode;
	children: ReactNode;
}

/**
 * Phone-width replacement for the icon rail: a slim top bar with the logo and
 * a menu button that opens the full menu as a slide-out panel.
 */
const MobileNavBar = ({ brand, children }: MobileNavBarProps) => {
	const { t } = useTranslation();
	const location = useLocation();
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const handleOpen = useCallback(() => setIsMenuOpen(true), []);
	const handleClose = useCallback(() => setIsMenuOpen(false), []);

	// Any navigation from the menu closes it, including a tap on the current page.
	useEffect(() => {
		setIsMenuOpen(false);
	}, [location.key]);

	return (
		<>
			<GlobalStyles styles={PHONE_GLOBAL_STYLES} />
			<AppBar
				position='fixed'
				elevation={0}
				sx={{
					height: MOBILE_BAR_HEIGHT,
					backgroundColor: "#fff",
					color: NAVY,
					borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
				}}
			>
				<Toolbar
					disableGutters
					sx={{ minHeight: `${MOBILE_BAR_HEIGHT}px !important`, px: 1, gap: 1 }}
				>
					<IconButton
						aria-label={t("mobile-nav_open-menu")}
						aria-expanded={isMenuOpen}
						aria-controls='mobile-nav-menu'
						onClick={handleOpen}
						sx={{ color: NAVY }}
					>
						<MenuIcon />
					</IconButton>
					<Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
						{brand}
					</Box>
				</Toolbar>
			</AppBar>
			<Drawer
				id='mobile-nav-menu'
				variant='temporary'
				anchor='left'
				open={isMenuOpen}
				onClose={handleClose}
				ModalProps={{ keepMounted: true }}
				PaperProps={{
					sx: {
						width: MENU_WIDTH,
						maxWidth: "85vw",
						color: "#2F4D8B",
						display: "flex",
						flexDirection: "column",
					},
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						height: MOBILE_BAR_HEIGHT,
						pl: 2,
						pr: 1,
						flexShrink: 0,
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center" }}>{brand}</Box>
					<IconButton
						aria-label={t("mobile-nav_close-menu")}
						onClick={handleClose}
						sx={{ color: NAVY }}
					>
						<CloseIcon />
					</IconButton>
				</Box>
				<Divider />
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						flex: 1,
						overflowY: "auto",
					}}
				>
					{children}
				</Box>
			</Drawer>
		</>
	);
};

export default MobileNavBar;
